const RELOAD_MARKER = 'avoschool:preload-reload-at'
const RELOAD_COOLDOWN_MS = 60_000

interface RecoveryOptions {
  target?: Pick<Window, 'addEventListener'>
  storage?: Pick<Storage, 'getItem' | 'setItem'>
  reload?: () => void
  now?: () => number
}

function readSessionStorage(): Pick<Storage, 'getItem' | 'setItem'> | undefined {
  try {
    return window.sessionStorage
  } catch {
    // Accessing the getter itself may throw in privacy-restricted contexts.
    return undefined
  }
}

/**
 * An already open tab can still run the previous build when a deployment
 * replaces its hashed lazy chunks. Vite emits `vite:preloadError` in that
 * situation. One reload obtains the non-cacheable current index.html; the
 * session marker prevents a broken deployment from causing a reload loop.
 */
export function installDeploymentRecovery(options: RecoveryOptions = {}): void {
  const target = options.target ?? window
  const storage = options.storage ?? readSessionStorage()
  const reload = options.reload ?? (() => window.location.reload())
  const now = options.now ?? Date.now
  let reloadRequested = false

  target.addEventListener('vite:preloadError', (event: Event) => {
    event.preventDefault()
    if (reloadRequested) return

    const timestamp = now()
    try {
      const stored = storage?.getItem(RELOAD_MARKER) ?? null
      const previous = stored === null ? Number.NaN : Number(stored)
      const elapsed = timestamp - previous
      if (Number.isFinite(previous) && elapsed >= 0 && elapsed < RELOAD_COOLDOWN_MS) return
      storage?.setItem(RELOAD_MARKER, String(timestamp))
    } catch {
      // Privacy modes may deny sessionStorage. The in-memory guard still
      // prevents repeated reloads for the lifetime of this document.
    }

    reloadRequested = true
    reload()
  })
}
